// src/utils/httpRequestHelper.js
import axios from 'axios';
import { useAuthStore } from '@/store/auth';
import notify from '@/utils/notify';
import router from '@/router';

const apiClient = axios.create({
    baseURL: '/api/v1',
    timeout: 60000,
    withCredentials: true, // Enable session cookies
});

// Request interceptor — attach x-app-brand and Accept-Language from localStorage
apiClient.interceptors.request.use(
    (config) => {
        const brand = localStorage.getItem('appBrand') || '1';
        config.headers['x-app-brand'] = brand;
        const lang = localStorage.getItem('app-language') || 'zh';
        config.headers['Accept-Language'] = lang === 'en' ? 'en' : 'zh';
        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor - handle status codes globally
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        const authStore = useAuthStore();

        if (error.response) {
            const status = error.response.status;

            switch (status) {
                case 401:
                    console.warn('Unauthorized - session expired');

                    // Only show message and redirect if user was logged in
                    if (authStore.isLoggedIn) {
                        notify.warning('Session expired. Please login again.');
                        authStore.logout();

                        // Avoid redirect loop - don't redirect if already on login page
                        if (router.currentRoute.value.path !== '/login') {
                            router.push({
                                path: '/login',
                                query: { redirect: router.currentRoute.value.fullPath }
                            });
                        }
                    }
                    break;

                case 400:
                    console.error('Bad Request:', error.response.data);
                    break;

                case 403:
                    console.error('Forbidden:', error.response.data);
                    notify.error('You do not have permission to access this resource');
                    break;

                case 404:
                    // Silently log — let each component handle 404 in its own catch block
                    console.error('Not Found:', error.response.data);
                    break;

                case 500:
                    console.error('Server Error:', error.response.data);
                    notify.error('Server error. Please try again later.');
                    break;

                default:
                    console.error('Error:', error.response.data);
                    notify.error(error.response.data?.message || 'An error occurred');
            }
        } else if (error.request) {
            console.error('Network Error:', error);
            notify.error('Network error. Please check your connection.');
        } else {
            console.error('Error:', error.message);
            notify.error(error.message || 'An error occurred');
        }

        return Promise.reject(error);
    }
);

export const getRequest = (url, params = {}, options = {}) => {
    const { headers, ...restOptions } = options;
    return apiClient.get(url, {
        params,
        headers: headers || {},
        ...restOptions,
    });
};

export const postRequest = (url, data = {}, options = {}) => {
    return apiClient.post(url, data, options);
};

export const patchRequest = (url, data = {}, options = {}) => {
    return apiClient.patch(url, data, options);
};

export const putRequest = (url, data = {}, options = {}) => {
    return apiClient.put(url, data, options);
};

export const deleteRequest = (url, options = {}) => {
    return apiClient.delete(url, options);
};

export { apiClient };

export default {
    getRequest,
    postRequest,
    patchRequest,
    putRequest,
    deleteRequest,
    apiClient
}
